/**
 * What a customer's users actually receive — AUTH-09.
 *
 * The templates could already render themselves for display. Nothing
 * called it, so the only way to find out what your users were sent was to
 * make one send - and a password reset is not a mail you can casually
 * trigger against somebody's real address.
 *
 * The HTML goes in a sandboxed iframe rather than into this page. Two
 * reasons, and the second is the one that matters day to day: a mail
 * template sets its own <body> background, padding and font, so dropping
 * it inline would either fight the dashboard's styles or be reformatted by
 * them - and a preview that does not look like the mail is not a preview.
 * The sandbox attribute, with nothing allowed, is the first reason.
 */

import { useState } from 'react'
import { useApi, useAsync } from '../lib/useApi.js'
import { ErrorBox, Loading } from './ui.js'

type Kind = 'password_reset' | 'email_verification'

const KINDS: { value: Kind; label: string }[] = [
  { value: 'password_reset', label: 'Password reset' },
  { value: 'email_verification', label: 'Confirm your address' },
]

export function EmailPreview({ projectId }: { projectId: string }) {
  const api = useApi()
  const [kind, setKind] = useState<Kind>('password_reset')
  const preview = useAsync(() => api.authEmailPreview(projectId, kind), [api, projectId, kind])

  return (
    <section style={{ marginTop: 24 }}>
      <h2>What your users receive</h2>
      <div className="field" style={{ maxWidth: 320 }}>
        <label htmlFor="email-preview-kind">Message</label>
        <select
          id="email-preview-kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as Kind)}
        >
          {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </div>

      {preview.error && <ErrorBox>{preview.error}</ErrorBox>}
      {preview.loading && !preview.data ? <Loading /> : preview.data && (
        <>
          <p className="sub small" style={{ margin: '0 0 4px' }}>
            Subject: <span className="mono">{preview.data.subject}</span>
          </p>
          <p className="sub small" style={{ margin: '0 0 12px' }}>
            {preview.data.deliverable
              ? <>Sent from <span className="mono">{preview.data.from}</span>. The link is a
                placeholder — a preview never mints a working one.</>
              : <>This deployment has no email provider, so none of this is sent. Somebody who
                forgets their password cannot reset it.</>}
          </p>
          <iframe
            title="Email preview"
            sandbox=""
            srcDoc={preview.data.html}
            style={{
              width: '100%', height: 460, border: '1px solid var(--border)',
              borderRadius: 8, background: '#fff',
            }}
          />
        </>
      )}
    </section>
  )
}
